import { getFieldLabel, getParentField } from "../accessHelpers.js";
import { splitPath } from "../objectPaths.js";
import { isRequired } from "./validation/isRequired.js";
/**
 * Returns the value of the equivalent "No" option for the given field (in
 * addition to blank). This is used to determine if the field should be shown.
 *
 * @param field The field to get the "No" value for.
 * @returns The value of the equivalent "No" option for the given field for
 *   comparing against the state.
 */
export function getNoValue(field) {
    let noValue = "";
    if (field.definition.typeName === "ZodUnion" && "options" in field) {
        noValue = String(field.options.find((o) => {
            if (!o.description)
                return false;
            const settings = o.settings;
            return settings && settings.isNoOption;
        })?.value || "");
    }
    return noValue;
}
/**
 * Takes a field's name and returns its current value from the state.
 *
 * @template T The type of the items the form state represents.
 * @param fieldName The fully qualified name of the field.
 * @param state The global state of the form.
 * @returns The current value of the field from the state.
 * @throws Error If the field name is missing, invalid, or the parent field is
 *   not an object.
 */
export function getStateValue(fieldName, state) {
    const invalidStateErrorPrefix = `Invalid state for field ${fieldName}:`;
    if (!fieldName) {
        throw new Error("Missing field name");
    }
    let stateChecking = state;
    const pathParts = splitPath(fieldName);
    if (pathParts.length === 1) {
        return state[fieldName];
    }
    /** The last path segment for tracking down errors */
    let lastSegment = "/";
    while (pathParts.length > 0) {
        const currentField = pathParts.shift();
        if (!currentField) {
            throw new Error(`${invalidStateErrorPrefix} Malformed path.`);
        }
        if (stateChecking === undefined)
            throw new Error(`${invalidStateErrorPrefix} The parent field "${lastSegment}" does not exist.`);
        if (!isPartialFormState(stateChecking)) {
            throw new Error(`${invalidStateErrorPrefix} The parent field "${lastSegment}" is not an object.`);
        }
        const arrayPathData = handleArrayPath(currentField, stateChecking, {
            fieldName,
            lastSegment,
        });
        if (arrayPathData) {
            stateChecking = arrayPathData;
        }
        else {
            stateChecking = stateChecking[currentField];
        }
        lastSegment = currentField;
    }
    return stateChecking;
}
/**
 * Checks if a path is an array path and navigates accordingly.
 *
 * @param currentField The field path segment to check.
 * @param stateChecking The current object level
 * @param meta Data regrading field paths and segments for more helpful errors.
 * @param meta.fieldName The name of the field the path is for.
 * @param meta.lastSegment The last segment of the path traversed.
 * @returns Null if not an array segment, or the next level at that array index.
 * @throws Error if the path is invalid.
 */
export function handleArrayPath(currentField, stateChecking, meta) {
    const invalidStateErrorPrefix = `Invalid state for field ${meta.fieldName}:`;
    const arrayIndexMatch = currentField?.match(/(.+)\[(\d+)]$/);
    if (arrayIndexMatch) {
        const actualFieldName = arrayIndexMatch[1];
        const arrayIndex = Number.parseInt(arrayIndexMatch[2]);
        if (!(actualFieldName in stateChecking)) {
            throw new Error(`${invalidStateErrorPrefix} The array field "${actualFieldName}" does not exist in "${meta.lastSegment}".`);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fieldValue = stateChecking[actualFieldName];
        if (!fieldValue || !Array.isArray(fieldValue))
            throw new Error(`${invalidStateErrorPrefix} The field "${actualFieldName}" is not an array.`);
        if (!fieldValue[arrayIndex])
            throw new Error(`${invalidStateErrorPrefix} There is no element with index ${arrayIndex} in the array "${actualFieldName}"`);
        return fieldValue[arrayIndex];
    }
    return null;
}
/**
 * Returns true if the state is a partial form state.
 *
 * @param state The value to check.
 * @returns True if the value is a partial form state.
 */
export function isPartialFormState(state) {
    return !!(state && typeof state === "object" && !(state instanceof Date));
}
/**
 * Returns true if the field should be shown in the form as the given coreType.
 *
 * @param field The field to check
 * @param state The current state of the form
 * @param allFields All of the fields in the form.
 * @returns True if the field should be shown in the form as the given coreType.
 */
export function shouldShow(field, state, allFields) {
    const fieldSettings = field.settings;
    if (!fieldSettings) {
        return true;
    }
    if (fieldSettings.hide) {
        return false;
    }
    if (!fieldSettings.dependsOn) {
        return true;
    }
    const parentField = getParentField(allFields, fieldSettings.dependsOn);
    const parentValue = getStateValue(parentField.path, state);
    return isRequired(parentField, fieldSettings.dependsOn, parentValue);
}
/**
 * Returns true if the field should be shown in the form as the given type
 * (ZodString, ZodBoolean, etc.).
 *
 * @param field The parsed field to check.
 * @param coreType The core type to check against (ZodString, ZodNumber, etc.)
 * @returns True if the field should be shown in the form as the given type
 *   (ZodString, ZodBoolean, etc.).
 */
export function fieldTypeMatches(field, coreType) {
    return field.definition.typeName === coreType;
}
/**
 * Returns the label, value, and key for the given field.
 *
 * @template T The type of the field.
 * @param field The field to get the meta for
 * @param state The current state of the form
 * @param errors Any errors for the field.
 * @param fieldNameOverride An optional field name to use instead of the
 *   field's. Useful for array fields.
 * @returns The label, value, and key for the given field.
 */
export function getFieldMeta(field, state, errors, fieldNameOverride) {
    const fieldSettings = field.settings;
    const label = getFieldLabel(field);
    let value = undefined;
    try {
        if (state)
            value = getStateValue(fieldNameOverride || field.path, state);
    }
    catch (_) {
        // Do nothing
    }
    if (typeof value === "undefined") {
        value = (field.defaultValue || "");
    }
    const required = !field.optional && field.defaultValue === null;
    const isDependent = !!(fieldSettings && fieldSettings.dependsOn);
    const error = errors && errors[field.field];
    return {
        label,
        value,
        key: field.field,
        required,
        isDependent,
        error,
        field,
    };
}
/**
 * Returns the current depth of the fieldset in the form.
 *
 * @param accessPath The base access path of the fieldset.
 * @returns The current depth of the fieldset in the form.
 */
export function getLevel(accessPath) {
    return splitPath(accessPath).length;
}
/**
 * Returns true if the field should be shown not be excluded from the form
 *
 * @template T The type of the field.
 * @param field The field to check.
 * @param ignoredFields The fields to ignore.
 * @returns True if the field should be shown not be excluded from the form
 */
export function isIgnoredField(field, ignoredFields) {
    return !!(ignoredFields && ignoredFields.includes(field.field));
}
//# sourceMappingURL=helpers.js.map